import React, { createContext, useEffect, useReducer } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios.js';
import { MatxLoading } from 'app/components';
import { Login, Profile } from 'app/api-ws/auth-ws';

const initialState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
  patientId: null
};

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }
  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;
};

const setSession = (accessToken, patientId) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('patientId', patientId);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    localStorage.setItem('patientId', patientId);
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user
      };
    }
    case 'LOGIN': {
      const { user, patientId } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
        patientId
      };
    }
    case 'UPDATE_PATIENT_ID': {
      const { patientId } = action.payload;

      return {
        ...state,
        patientId
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'REGISTER': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const login = async (email, password) => {
    const loginRequest = {
      email: email,
      password: password
    };
    const response = await Login(loginRequest);
    const { token, user } = response.data;

    setSession(token);

    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
    return user;
  };
  const updatePatientId = (patientId) => {
    dispatch({
      type: 'UPDATE_PATIENT_ID',
      payload: {
        patientId
      }
    });
  };
  const logout = () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const storedPatientId = localStorage.getItem('patientId');
        const profileRequest = {
          authToken: accessToken
        };

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          const response = await Profile(profileRequest);
          const { data } = response.data;

          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: true,
              user: data,
              patientId: storedPatientId
            }
          });
        } else {
          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INIT',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    })();
  }, []);

  if (!state.isInitialised) {
    return <MatxLoading />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        updatePatientId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
